// Asistente de Re-empaque: cambia las fracciones por presentación de un producto
// de forma controlada (conteo → ajuste → cambio → re-alinea costo/precio, preserva
// el valor). Inventario → Operaciones → Re-empaque. Solo admin (nivel 5).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PackageOpen, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { BuscadorProducto, Campo, ProductoOpt, fieldCls, invRpc, invalidateInventario, money, num, productosOptList } from './shared';

interface Bodega { location_id: string; bodega: string; unidad_venta: string; base: number; costo_promedio: number; valor: number; cajas: number }
interface Estado {
  producto_id: string; codigo: string; nombre: string; presentacion: string | null;
  fracciones: number; maneja_lote: boolean; precio_base: number; costo_referencial: number;
  bodegas: Bodega[]; valor_total: number;
}

export function Reempaque() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList });
  const [prodId, setProdId] = useState('');

  const { data: estado, isFetching } = useQuery<Estado>({
    queryKey: ['inv-reempaque-estado', prodId], enabled: !!prodId,
    queryFn: () => invRpc<Estado>('inv_reempaque_estado', { _producto: prodId }),
  });

  const [nuevaFracc, setNuevaFracc] = useState('');
  const [nuevoPrecio, setNuevoPrecio] = useState('');
  const [motivo, setMotivo] = useState('');
  const [conteos, setConteos] = useState<Record<string, string>>({});

  useEffect(() => {
    if (estado) {
      setNuevaFracc(String(estado.fracciones));
      setNuevoPrecio(String(estado.precio_base ?? ''));
      setMotivo('');
      const c: Record<string, string> = {};
      estado.bodegas.forEach((b) => { c[b.location_id] = String(b.base); });
      setConteos(c);
    }
  }, [estado]);

  const nf = Math.max(1, Number(nuevaFracc) || 0);
  const cambia = estado && nf !== estado.fracciones && nf >= 1;

  const registrar = useMutation({
    mutationFn: () => invRpc<{ codigo: string; valor_antes: number; valor_despues: number; costo_ref_nuevo: number; avisos: { aviso: string }[] }>('inv_reempaque', {
      _payload: {
        producto_id: prodId, fracciones: nf, motivo,
        precio_base: nuevoPrecio === '' ? null : Number(nuevoPrecio),
        conteos: (estado?.bodegas ?? []).map((b) => ({ location_id: b.location_id, unidades: Number(conteos[b.location_id] ?? b.base) })),
      },
    }),
    onSuccess: (r) => {
      toast.success(`Re-empaque ${r.codigo} · valor ${money(r.valor_antes)} → ${money(r.valor_despues)}`);
      (r.avisos ?? []).forEach((a) => toast.warning(a.aviso));
      invalidateInventario(qc);
      void qc.invalidateQueries({ queryKey: ['inv-reempaque-estado', prodId] });
      void qc.invalidateQueries({ queryKey: ['inv-productos-opt'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const listo = !!estado && cambia && motivo.trim() !== '';

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><PackageOpen className="w-5 h-5" /> Re-empaque (cambiar fracciones)</h1>

      <Card>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4">
          <Campo label="Producto" className="md:col-span-3">
            <BuscadorProducto productos={productos} value={prodId} onChange={setProdId} />
          </Campo>
        </CardContent>
      </Card>

      {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}

      {estado && (
        <>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">{estado.codigo} · {estado.nombre}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><span className="text-muted-foreground">Presentación</span><div className="font-medium">{estado.presentacion || '—'}</div></div>
                <div><span className="text-muted-foreground">Fracciones actuales</span><div className="font-medium">{estado.fracciones}</div></div>
                <div><span className="text-muted-foreground">Precio base (caja)</span><div className="font-medium">{money(estado.precio_base)}</div></div>
                <div><span className="text-muted-foreground">Valor total</span><div className="font-medium">{money(estado.valor_total)}</div></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Campo label="Nuevas fracciones *"><input type="number" min={1} step={1} className={fieldCls} value={nuevaFracc} onChange={(e) => setNuevaFracc(e.target.value)} /></Campo>
                <Campo label="Nuevo precio base (caja)"><input type="number" min={0} step="0.01" className={fieldCls} value={nuevoPrecio} onChange={(e) => setNuevoPrecio(e.target.value)} placeholder="Opcional — deja el actual" /></Campo>
                <Campo label="Motivo *"><input className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Proveedor cambió empaque, corrección…" /></Campo>
              </div>

              {estado.maneja_lote && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Producto con lote: si el conteo físico difiere del sistema, esa bodega NO se ajusta aquí (se avisa) — reconcíliala en <b>Ajustes</b> indicando el lote.
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Existencias por bodega · impacto del cambio</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Bodega</th><th className="py-2 pr-3 text-right">Stock base (fracc.)</th>
                  <th className="py-2 pr-3 text-right">Cajas (÷{estado.fracciones})</th>
                  <th className="py-2 pr-3 text-right">Cajas (÷{nf})</th>
                  <th className="py-2 pr-3 text-right">Valor</th>
                  <th className="py-2 pr-3 text-right w-32">Conteo físico (fracc.)</th>
                </tr></thead>
                <tbody>
                  {estado.bodegas.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Sin existencias</td></tr>}
                  {estado.bodegas.map((b) => {
                    const cont = Number(conteos[b.location_id] ?? b.base);
                    const dif = cont - Number(b.base);
                    return (
                      <tr key={b.location_id} className="border-b last:border-0">
                        <td className="py-2 pr-3">{b.bodega} <span className="text-xs text-muted-foreground">({b.unidad_venta})</span></td>
                        <td className="py-2 pr-3 text-right tabular-nums">{num(b.base, 2)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">{num(Number(b.base) / estado.fracciones, 2)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums font-medium">{num(Number(b.base) / nf, 2)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{money(b.valor)}</td>
                        <td className="py-2 pr-3 text-right">
                          <input type="number" step="0.0001" className={`${fieldCls} h-8 w-28 text-right ml-auto ${dif !== 0 ? 'border-amber-500' : ''}`}
                            value={conteos[b.location_id] ?? ''} onChange={(e) => setConteos((s) => ({ ...s, [b.location_id]: e.target.value }))} />
                          {dif !== 0 && <div className="text-[11px] text-amber-700 dark:text-amber-400">dif {dif > 0 ? '+' : ''}{num(dif, 2)}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-muted-foreground">
                El stock base (fracciones) no cambia por re-empacar; cambia cómo se lee en cajas y el costo/precio por presentación.
                El <b>valor total se preserva</b>. Si ajustas el conteo físico, se posteará un ajuste por la diferencia (salvo productos con lote).
              </p>
              <div className="mt-3 flex justify-end">
                <Button isLoading={registrar.isPending} disabled={!listo}
                  onClick={async () => { if (await dlg.confirmar(`¿Re-empacar ${estado.codigo} de ${estado.fracciones} a ${nf} fracciones?`, { detalle: 'El valor se preserva.' })) registrar.mutate(); }}>
                  Aplicar re-empaque
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
